import {
  Fragment,
  useCallback,
  useEffect,
  useState,
  type PropsWithChildren,
} from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { QuickFilterSelector } from "@features/quick-filter-selector";
import { SyncViewReordering } from "@features/saved-views/sync-view-reordering";
import { ShareButton } from "@features/shared";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { useSavedViews } from "../hooks/saved-view";

import { CreateSavedViewModal } from "./create-saved-view-modal";
import { SelectedSavedView } from "./selected-saved-view";

export function SavedViewsControl() {
  const { user } = useUser();

  if (user.featureFlags.enableDashboardV2) {
    return (
      <div className="flex flex-row items-end ml-5 w-full">
        <SavedViewSelector />
        <SavedViewCreator />
      </div>
    );
  }
  return (
    <div className="flex flex-row items-center ml-5">
      <SavedViewSelector />
      <SavedViewCreator />
    </div>
  );
}

function SavedViewCreator() {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { hasUnsavedChanges } = useSavedViews();
  return (
    <>
      <Button
        onClick={() => setModalIsOpen(true)}
        className="ml-3 mr-2"
        disabled={!hasUnsavedChanges}
      >
        <div
          className={clsx("flex flex-row items-center", {
            "text-v2-secondary": !hasUnsavedChanges,
          })}
        >
          <span>Save</span>
          <i className="ml-3 text-lg fa-light fa-sharp fa-circle-plus" />
        </div>
      </Button>
      <CreateSavedViewModal
        open={modalIsOpen}
        closeModal={() => setModalIsOpen(false)}
      />
    </>
  );
}

function SavedViewSelector() {
  const { isDemoMode } = usePrefs();
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();

  const { setSelectedSaveView, availableSavedViews, selectedSavedView } =
    useSavedViews();
  const [savedViewManagerModalIsOpen, setSavedViewMangerModalIsOpen] =
    useState(false);

  const savedViewOptions = availableSavedViews.map((view) => ({
    id: view.id,
    name: view.name,
    isDefault: view.isDefault,
  }));

  const selectedOption = {
    id: selectedSavedView.id,
    name: selectedSavedView.name,
    isDefault: selectedSavedView.isDefault,
  };

  if (user.featureFlags.enableDashboardV2) {
    return (
      <div className="flex flex-row items-center w-full">
        <SavedViewsModal
          open={savedViewManagerModalIsOpen}
          closeModal={() => setSavedViewMangerModalIsOpen(false)}
        />
        <QuickFilterSelector
          options={savedViewOptions}
          value={selectedOption}
          onChange={(option) => {
            setSelectedSaveView(option.id, { event: "select" });
            prettyLogEventPage({
              domain: "Saved Views",
              action: "onClick",
              additionalActionInfo: "Specific saved view",
              data: option.name,
            });
          }}
          tooltipComponent={(option) => (
            <div className="text-sm">
              <div className="font-semibold">{option.name}</div>
              {option.id === availableSavedViews[0]?.id && (
                <div className="text-v2-secondary">Default view</div>
              )}
            </div>
          )}
        />
        <button
          className={clsx(
            "ml-2 flex flex-row items-center text-cosmic",
            "cursor-pointer text-sm hover:bg-cosmic-4 hover:rounded-2xl px-3 py-1",
          )}
          onClick={() => {
            setSavedViewMangerModalIsOpen(true);
            prettyLogEventPage({
              action: "onOpen",
              domain: "Saved Views",
              additionalActionInfo: "manager",
            });
          }}
        >
          <i className="mr-2.5 text-lg fa-regular fa-sharp fa-gear" />
          <div>MANAGE</div>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center">
      <SavedViewsModal
        open={savedViewManagerModalIsOpen}
        closeModal={() => setSavedViewMangerModalIsOpen(false)}
      />
      <GenericTooltip content={selectedSavedView.name} noInfoCircle>
        <div className="text-2xl font-semibold min-w-[15rem] max-w-[20rem] truncate">
          {generateAnonBase64Hash({
            value: selectedSavedView.name,
            prefix: "quick-filter",
            doAnon: isDemoMode && !selectedSavedView.isDefault,
          })}
        </div>
      </GenericTooltip>
      <Menu as="div" className="ml-5 mr-2 relative">
        {({ open }) => (
          <>
            <MenuButton
              onClick={() =>
                prettyLogEventPage({
                  action: "onClick",
                  domain: "Saved Views",
                  additionalActionInfo: "Selector",
                  data: {
                    pageContext: { selectedSavedViewId: selectedSavedView.id },
                  },
                })
              }
            >
              <div
                className={clsx(
                  "flex items-center justify-center h-[2.3rem] w-[2.3rem]",
                  "border-v2-secondary border-[1px] rounded-full",
                  {
                    "bg-[#F3F5FF]": open,
                    "bg-v2-primary": !open,
                    "hover:bg-[#F3F5FF]": true,
                  },
                )}
              >
                <i
                  className={clsx(
                    "font-semibold fa-regular fa-sharp fa-sharp",
                    "cursor-pointer text-v2-action text-sm ",
                    {
                      "fa-chevron-down": !open,
                      "fa-chevron-up": open,
                    },
                  )}
                />
              </div>
            </MenuButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform ease-out duration-100"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems
                className={clsx(
                  "absolute -right-5 z-10 mt-2 w-[19rem] origin-top-right",
                  "rounded-[8px] bg-v2-primary elevation-1 pb-5 pt-4 pl-6 pr-2",
                )}
              >
                <div className="font-semibold flex flex-row w-full items-center">
                  <div>Saved Views</div>
                  <button
                    className={clsx(
                      "ml-auto flex flex-row items-center text-cosmic",
                      "cursor-pointer text-sm hover:bg-cosmic-4 hover:rounded-2xl px-3 py-1",
                    )}
                    onClick={() => {
                      setSavedViewMangerModalIsOpen(true);
                      prettyLogEventPage({
                        action: "onOpen",
                        domain: "Saved Views",
                        additionalActionInfo: "manager",
                      });
                    }}
                  >
                    <i className="mr-2.5 text-lg fa-regular fa-sharp fa-gear" />
                    <div>MANAGE</div>
                  </button>
                </div>
                <div className="max-h-[35rem] overflow-y-auto">
                  {availableSavedViews.map((view, index) => {
                    return (
                      <MenuItem key={view.name}>
                        <MenuItemContent
                          title={generateAnonBase64Hash({
                            value: view.name,
                            prefix: "quick-filter",
                            doAnon: isDemoMode && !view.isDefault,
                          })}
                          isDefault={!index}
                          isTemplate={view.isDefault}
                          active={view.name === selectedSavedView.name}
                          onClick={() => {
                            setSelectedSaveView(view.id, { event: "select" });
                            prettyLogEventPage({
                              domain: "Saved Views",
                              action: "onClick",
                              additionalActionInfo: "Specific saved view",
                              data: view.name,
                            });
                          }}
                        />
                      </MenuItem>
                    );
                  })}
                </div>
              </MenuItems>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  );
}

function SavedViewsModal({
  open,
  closeModal,
}: {
  open: boolean;
  closeModal: () => void;
}) {
  const prettyLogEventPage = usePrettyLogEventPage();

  const {
    availableSavedViews,
    saveAllViews,
    loadingSavedViewUpsert,
    upsertSavedViewOrderingToAllAccountManagers,
  } = useSavedViews();
  const [modifiedSaveViews, setModifiedSavedViews] =
    useState(availableSavedViews);
  const sensors = useSensors(useSensor(PointerSensor));

  const onClose = useCallback(() => {
    setModifiedSavedViews(availableSavedViews);
    closeModal();
    prettyLogEventPage({
      action: "onClose",
      domain: "Saved Views",
      additionalActionInfo: "Manager",
    });
  }, [availableSavedViews, closeModal, prettyLogEventPage]);

  useEffect(() => {
    setModifiedSavedViews(availableSavedViews);
  }, [availableSavedViews]);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = modifiedSaveViews.findIndex(
        ({ id }) => id === active.id,
      );
      const newIndex = modifiedSaveViews.findIndex(({ id }) => id === over?.id);

      const newOrdering = arrayMove(modifiedSaveViews, oldIndex, newIndex);
      setModifiedSavedViews(newOrdering);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      titleContent={
        <div className="text-lg font-semibold">Manage saved views</div>
      }
      bodyClassName="h-[calc(100%-220px)]"
      contentClassName="h-[calc(100%-80px)] overflow-hidden"
    >
      <div className="text-subtitle font-light">
        Saved views are accessible by all users on your Northbeam account.
        Share, rename, or arrange the order these views appear in the menu. The
        view sorted to the top will become the default view.
      </div>
      <div
        className={clsx(
          "border border-gray+2 mt-7 h-full rounded-lg self-center relative overflow-y-auto",
        )}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[
            restrictToVerticalAxis,
            restrictToFirstScrollableAncestor,
          ]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={modifiedSaveViews.map((view) => view.id)}>
            {modifiedSaveViews.map((view, index) => (
              <SelectedSavedView
                key={view.id}
                isDefault={!index}
                savedView={view}
                deleteView={(id) => {
                  const filteredViews = modifiedSaveViews.filter(
                    (i) => i.id !== id,
                  );
                  setModifiedSavedViews(filteredViews);
                }}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
      <div className="flex justify-between flex-row">
        <SyncViewReordering
          updateSavedViewOrderingToAllAccountManagers={async () =>
            await upsertSavedViewOrderingToAllAccountManagers(modifiedSaveViews)
          }
          className="w-[15rem]"
          includeReorderingTitle={false}
        />
        <div className="flex items-center justify-end gap-2 mt-6">
          <TertiaryButton onClick={onClose} disabled={loadingSavedViewUpsert}>
            Cancel
          </TertiaryButton>
          <PrimaryButton
            loading={loadingSavedViewUpsert}
            isDisabled={loadingSavedViewUpsert}
            onClick={async () => {
              await saveAllViews(modifiedSaveViews);
              closeModal();
              prettyLogEventPage({
                action: "onSubmit",
                domain: "Saved Views",
                additionalActionInfo: "Manager",
                data: modifiedSaveViews,
              });
            }}
          >
            Save
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}

function MenuItemContent({
  title,
  isDefault,
  isTemplate,
  active,
  onClick,
}: {
  title: string;
  isDefault: boolean;
  isTemplate: boolean;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex flex-row items-center mt-4">
      <div className="w-[2rem]">
        {active && <i className="fa-regular fa-sharp fa-check text-cosmic" />}
      </div>
      <div className="flex flex-col cursor-pointer" onClick={onClick}>
        <div className="truncate w-[14rem]">{title}</div>
        <div className="flex flex-row text-bold text-sm text-[#BAC4D4]">
          {isDefault && <div>default</div>}
          {isDefault && isTemplate && <div className="mx-1">and</div>}
          {isTemplate && <div>template</div>}
        </div>
      </div>
    </div>
  );
}

export function Button({
  children,
  onClick,
  className,
  disabled,
}: PropsWithChildren<{
  onClick: () => void;
  className?: string;
  disabled?: boolean;
}>) {
  return (
    <button
      className={clsx("py-2 px-2 text-cosmic", className)}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
